import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import BookNowLink from '../../components/booking/BookNowLink';
import { fetchDiscoverCampaign } from '../../api/discover';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { resolveBookBranchId } from '../../utils/serviceBranches';

const OfferDetailPage = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['discover-campaign', campaignId],
    queryFn: () => fetchDiscoverCampaign(campaignId),
    enabled: !!campaignId,
  });

  const campaign = data?.campaign;
  const salon = data?.salon;
  const services = data?.services || [];
  const products = data?.products || [];

  const discountLabel = (mode, value) =>
    mode === 'PERCENTAGE'
      ? t('marketplace.percentOff', { value: Number(value) })
      : t('marketplace.flatOff', { value: Number(value || 0).toLocaleString() });

  if (isLoading) {
    return (
      <MarketplaceLayout>
        <p className="p-8 text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>
      </MarketplaceLayout>
    );
  }

  if (error || !campaign || !salon) {
    return (
      <MarketplaceLayout>
        <div className="mx-auto max-w-lg px-6 py-12 text-center">
          <p className="text-zinc-700 dark:text-zinc-300">{t('marketplace.offerUnavailable')}</p>
          <Link to="/marketplace" className="mt-4 inline-block text-sm text-glow-400 hover:underline">
            {t('marketplace.backToMarketplace')}
          </Link>
        </div>
      </MarketplaceLayout>
    );
  }

  const tenantLink = `/explore/${salon.tenantId}`;
  const marketplaceLink = `/marketplace?tenantId=${salon.tenantId}`;

  return (
    <MarketplaceLayout>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <Link to="/marketplace" className="text-sm text-glow-400 hover:underline">
          ← {t('common.marketplace')}
        </Link>

        <div className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-gradient-to-r from-glow-600/25 via-purple-600/10 to-emerald-600/20 p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-700 dark:text-zinc-300/90">
                {t('marketplace.offer')}
              </p>
              <h1 className="mt-1 text-2xl font-semibold sm:text-3xl">
                {campaign.bannerText || campaign.name}
              </h1>
              {campaign.description && (
                <p className="mt-3 max-w-2xl text-sm text-zinc-700 dark:text-zinc-200/85">
                  {campaign.description}
                </p>
              )}
              {campaign.endAt && (
                <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-200/80">
                  {t('marketplace.ends', { date: new Date(campaign.endAt).toLocaleString() })}
                </p>
              )}
            </div>
            <span className="shrink-0 rounded-full bg-black/15 px-4 py-2 text-sm font-semibold dark:bg-white/15">
              {discountLabel(campaign.discountMode, campaign.discountValue)}
            </span>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
          <div className="flex flex-wrap items-start gap-4">
            {salon.logo ? (
              <img src={salon.logo} alt="" className="h-16 w-16 rounded-xl object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800 text-lg font-semibold text-zinc-600 dark:text-zinc-400">
                {salon.businessName?.[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-zinc-600 dark:text-zinc-500">{t('marketplace.fromThisSalon')}</p>
              <h2 className="text-xl font-semibold">{salon.businessName}</h2>
              {salon.tagline && (
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{salon.tagline}</p>
              )}
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">{salon.address}</p>
              {salon.phone && (
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{salon.phone}</p>
              )}
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end">
              <Link
                to={tenantLink}
                className="rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-center text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                {t('marketplace.viewSalonProfile')}
              </Link>
              <Link
                to={marketplaceLink}
                className="rounded-lg bg-glow-600 px-4 py-2 text-center text-sm text-white hover:bg-glow-500"
              >
                {t('marketplace.shopThisSalon')}
              </Link>
            </div>
          </div>
        </div>

        {services.length > 0 && (
          <section className="mt-10">
            <h3 className="text-lg font-semibold">{t('marketplace.servicesOnOffer')}</h3>
            <ul className="mt-4 space-y-3">
              {services.map((s) => (
                <li
                  key={s._id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
                >
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-zinc-600 dark:text-zinc-500">
                      {t('common.duration', { minutes: s.duration })} · {s.category}
                    </p>
                    {s.campaignPricing ? (
                      <p className="mt-2 text-sm">
                        <span className="text-zinc-500 line-through">
                          {t('common.currency', { amount: Number(s.campaignPricing.basePrice || 0).toLocaleString() })}
                        </span>
                        <span className="ml-2 font-semibold text-glow-300">
                          {t('common.currency', { amount: Number(s.campaignPricing.finalPrice || 0).toLocaleString() })}
                        </span>
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">
                        {t('common.currency', { amount: Number(s.price || 0).toLocaleString() })}
                      </p>
                    )}
                  </div>
                  <BookNowLink
                    tenantId={salon.tenantId}
                    branchId={resolveBookBranchId(s)}
                    serviceId={s._id}
                    className="rounded-lg bg-glow-600 px-4 py-2 text-sm text-white"
                  >
                    {t('marketplace.book')}
                  </BookNowLink>
                </li>
              ))}
            </ul>
          </section>
        )}

        {products.length > 0 && (
          <section className="mt-10">
            <h3 className="text-lg font-semibold">{t('marketplace.productsOnOffer')}</h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {products.map((p) => (
                <li
                  key={p._id}
                  className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
                >
                  <Link to={`/marketplace/products/${p._id}`} className="font-medium hover:text-glow-400">
                    {p.name}
                  </Link>
                  {p.campaignPricing ? (
                    <p className="mt-2 text-sm">
                      <span className="text-zinc-500 line-through">
                        {t('common.currency', { amount: Number(p.campaignPricing.basePrice || 0).toLocaleString() })}
                      </span>
                      <span className="ml-2 font-semibold text-glow-300">
                        {t('common.currency', { amount: Number(p.campaignPricing.finalPrice || 0).toLocaleString() })}
                      </span>
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-glow-300">{t('common.currency', { amount: Number(p.price || 0).toLocaleString() })}</p>
                  )}
                  <Link
                    to={`/marketplace/products/${p._id}`}
                    className="mt-3 inline-block text-xs text-glow-400 hover:underline"
                  >
                    {t('marketplace.viewProduct')}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {services.length === 0 && products.length === 0 && (
          <p className="mt-8 text-center text-sm text-zinc-600 dark:text-zinc-500">
            {t('marketplace.noOfferItems')}
          </p>
        )}
      </div>
    </MarketplaceLayout>
  );
};

export default OfferDetailPage;
